// Super Admin App — completely separate from the regular school/admin login
// at "/". Lives at "/super-admin", own session storage (super_admin_token,
// not school_token). Shares the same Zorvexl design system as the school
// admin panel — same shell, type, components — but runs in the "clearance"
// bronze accent instead of transit teal, signalling elevated privilege
// without reading as a different product.

import { useState } from 'react';
import { School, KeyRound } from 'lucide-react';
import { requestSuperAdminCode, verifySuperAdminCode } from './api/client';
import SchoolsPanel      from './components/SchoolsPanel';
import SchoolAdminsPanel from './components/SchoolAdminsPanel';
import Logo from './ui/Logo';
import Button from './ui/Button';
import Sidebar from './ui/Sidebar';
import TopBar from './ui/TopBar';

const TABS = [
  { key: 'schools', label: 'Schools',       icon: School },
  { key: 'admins',  label: 'School Admins', icon: KeyRound },
];

export default function SuperAdminApp() {
  const [token, setToken] = useState(() => localStorage.getItem('super_admin_token'));
  const [active, setActive] = useState('schools');

  const [step, setStep] = useState('email'); // 'email' | 'code'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function handleRequestCode(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      await requestSuperAdminCode(email.trim());
      setStep('code');
    } catch (e2) {
      setErr(e2.response?.data?.error || 'Could not send verification code');
    } finally { setBusy(false); }
  }

  async function handleVerifyCode(e) {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const data = await verifySuperAdminCode(email.trim(), code.trim());
      // A leftover school_token from the regular admin panel takes priority
      // in api/client.js's interceptor (checked first) — clear it so this
      // browser can't end up silently calling super-admin routes with a
      // school_admin-role token and getting a confusing 403.
      localStorage.removeItem('school_token');
      localStorage.removeItem('school_name');
      localStorage.setItem('super_admin_token', data.token);
      setToken(data.token);
    } catch (e2) {
      setErr(e2.response?.data?.error || 'Invalid or expired code');
    } finally { setBusy(false); }
  }

  function handleLogout() {
    localStorage.removeItem('super_admin_token');
    setToken(null);
    setStep('email'); setEmail(''); setCode(''); setErr('');
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center px-6">
        <div className="w-full max-w-[408px] bg-paper-raised rounded-l shadow-2 border border-line p-10 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-clearance-deep via-clearance to-clearance-deep" />
          <div className="flex justify-center mb-5"><Logo tier="clearance" size={56} showWordmark={false} /></div>
          <h1 className="font-display font-extrabold text-2xl text-center text-ink mb-1">Super Admin</h1>
          <p className="text-sm text-center text-ink-soft mb-7">System-wide access — verified by email, not a shared key</p>

          {err && <div className="bg-critical-tint border border-critical/20 text-critical text-sm px-3 py-2.5 rounded-s mb-4">{err}</div>}

          {step === 'email' && (
            <form onSubmit={handleRequestCode} className="flex flex-col gap-1">
              <label className="text-[11px] font-bold uppercase tracking-wide text-ink-soft">Email Address</label>
              <input
                className="border border-line rounded-s px-3 py-2.5 text-[15px] bg-paper focus:outline-none focus:ring-2 focus:ring-clearance focus:bg-white mb-3"
                type="email" placeholder="you@example.com" value={email}
                onChange={e => setEmail(e.target.value)} required autoFocus
              />
              <Button variant="clearance" disabled={busy} className="w-full">
                {busy ? 'Sending code…' : 'Send Verification Code'}
              </Button>
            </form>
          )}

          {step === 'code' && (
            <form onSubmit={handleVerifyCode} className="flex flex-col gap-1">
              <label className="text-[11px] font-bold uppercase tracking-wide text-ink-soft">6-Digit Code</label>
              <p className="text-xs text-ink-soft -mt-0.5 mb-2">Sent to {email}</p>
              <input
                className="border border-line rounded-s px-3 py-3 text-center text-2xl font-bold tracking-[0.5em] pl-5 bg-paper focus:outline-none focus:ring-2 focus:ring-clearance focus:bg-white mb-3"
                inputMode="numeric" maxLength={6} placeholder="••••••" value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, ''))} required autoFocus
              />
              <Button variant="clearance" disabled={busy} className="w-full">
                {busy ? 'Verifying…' : 'Verify & Enter'}
              </Button>
              <button
                type="button"
                className="w-full mt-1.5 py-2 text-xs text-ink-soft hover:text-clearance-deep"
                onClick={() => { setStep('email'); setCode(''); setErr(''); }}
              >
                ← Use a different email / resend
              </button>
            </form>
          )}

          <p className="text-center text-xs text-ink-soft mt-6 pt-5 border-t border-line-soft">
            <a href="/" className="text-clearance-deep font-semibold hover:underline">← Back to school admin login</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-paper">
      <Sidebar items={TABS} active={active} onSelect={setActive} tier="clearance" />
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar title="Platform Overview" tierLabel="Super Admin" tier="clearance" onLogout={handleLogout} />
        <div className="p-6 overflow-x-auto">
          {active === 'schools' && <SchoolsPanel />}
          {active === 'admins'  && <SchoolAdminsPanel />}
        </div>
      </div>
    </div>
  );
}
